import { createStore } from '@/shared/stores/config';
import type { ENudgeId } from './types';

/**
 * Nudge management store state.
 *
 * Tracks which nudge conditions are currently active (set by pages/modules)
 * and provides actions to register or clear conditions. The NudgeManager
 * component reads this to determine which nudge to display.
 */
interface INudgeStoreState {
  /**
   * Map of nudge ID → whether its activation condition is currently met.
   * Pages call `setCondition` to register, and the NudgeManager selects
   * the highest-priority active nudge that hasn't been dismissed.
   */
  conditions: Partial<Record<ENudgeId, boolean>>;

  /**
   * Registers or updates a nudge condition.
   *
   * @param nudgeId - Nudge identifier
   * @param isActive - Whether the nudge's condition is currently met
   */
  setCondition: (nudgeId: ENudgeId, isActive: boolean) => void;

  /**
   * Clears a nudge condition (e.g., when a page unmounts).
   *
   * @param nudgeId - Nudge identifier
   */
  clearCondition: (nudgeId: ENudgeId) => void;
}

/**
 * Zustand store for managing nudge activation conditions.
 *
 * @example
 * ```ts
 * const setCondition = useNudgeStore(selectSetNudgeCondition);
 * setCondition(ENudgeId.CREATE_FIRST_DRAFT, !hasDrafts);
 * ```
 */
export const useNudgeStore = createStore<INudgeStoreState>(
  'nudge-store',
  (_, get, api) => ({
    conditions: {},

    setCondition: (nudgeId: ENudgeId, isActive: boolean) => {
      const current = get().conditions;
      if (current[nudgeId] === isActive) return;

      api.setState({ conditions: { ...current, [nudgeId]: isActive } }, false, {
        type: 'nudge/setCondition',
        nudgeId,
        isActive,
      });
    },

    clearCondition: (nudgeId: ENudgeId) => {
      const current = get().conditions;
      if (!(nudgeId in current)) return;

      const { [nudgeId]: _, ...rest } = current;
      api.setState({ conditions: rest }, false, {
        type: 'nudge/clearCondition',
        nudgeId,
      });
    },
  }),
);

/** Selects the full conditions map. */
export const selectNudgeConditions = (state: INudgeStoreState) =>
  state.conditions;

/** Selects the action to set a nudge condition. */
export const selectSetNudgeCondition = (state: INudgeStoreState) =>
  state.setCondition;

/** Selects the action to clear a nudge condition. */
export const selectClearNudgeCondition = (state: INudgeStoreState) =>
  state.clearCondition;
